import { MiniGrid } from "./MiniGrid";
import { MarketClock } from "./MarketClock";
import { Money, Tag } from "./primitives";
import { fmtMicro, shortAddr } from "./mkt-format";
import type { ArtworkSummary, MarketplaceConfig } from "../../lib/marketplace-api";
import { symbolFor } from "./mkt-format";
import { useT } from "../../i18n";

export function placementCells(p: { anchor_x: number; anchor_y: number; width: number; height: number } | null): [number, number][] {
  if (!p) return [];
  const out: [number, number][] = [];
  for (let y = p.anchor_y; y < p.anchor_y + p.height; y++)
    for (let x = p.anchor_x; x < p.anchor_x + p.width; x++) out.push([x, y]);
  return out;
}

export function ArtworkCard({
  artwork,
  marketNow,
  config,
  onOpen,
  testId = "artwork-card",
}: {
  artwork: ArtworkSummary;
  marketNow: number;
  config?: MarketplaceConfig | null;
  onOpen: () => void;
  testId?: string;
}) {
  const t = useT();
  const cells = placementCells(artwork.placement);
  const auction = artwork.auction;
  const sym = auction ? symbolFor(auction.currency, config) : "USDC";
  return (
    <button
      type="button"
      data-testid={testId}
      data-artwork-id={artwork.artwork_id}
      onClick={onOpen}
      className="group text-left neon-glass rounded-lg border border-border-divider overflow-hidden hover:border-neon-teal/40 hover:shadow-glow-teal-sm transition-[border-color,box-shadow] duration-fast"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-elevated/40">
        <MiniGrid cells={cells} zoneClass="fill-neon-teal" />
        <div className="absolute top-2.5 left-2.5">
          {auction ? <Tag tone="coral" testId="artwork-card-auction-tag">{t("marketplace.artworks.auction")}</Tag> : <Tag tone="teal">{t("marketplace.artworks.openToOffers")}</Tag>}
        </div>
        <div className="absolute top-2.5 right-2.5"><Tag tone="muted">{t("marketplace.artworks.royalty", { n: artwork.royalty_percent })}</Tag></div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-display text-base text-text-primary truncate">{artwork.title}</span>
          <span className="font-mono text-[10px] text-text-muted shrink-0">{artwork.width}×{artwork.height}</span>
        </div>
        <div className="flex items-end justify-between gap-2">
          {auction ? (
            <Money amount={fmtMicro(auction.highest_bid_micro || auction.reserve_micro)} symbol={sym} className="text-base text-neon-teal" />
          ) : (
            <span className="font-mono text-[11px] text-text-muted">{t("marketplace.artworks.by", { addr: shortAddr(artwork.creator) })}</span>
          )}
          {auction && <MarketClock endMarketTs={auction.end_market_ts} marketNow={marketNow} antiSnipeWindow={auction.anti_snipe_window} testId="artwork-card-clock" />}
        </div>
      </div>
    </button>
  );
}
